"use client";

import { Feature1 } from "@/app/(public)/(landing)/components/Feature1";
import { Feature2 } from "@/app/(public)/(landing)/components/Feature2";
import { Feature3 } from "@/app/(public)/(landing)/components/Feature3";
import { Feature4 } from "@/app/(public)/(landing)/components/Feature4";
import Hero from "@/app/(public)/(landing)/components/Hero";
import { PromoBanner } from "@/app/(public)/(landing)/components/PromoBanner";
import { WhoWeBuildFor } from "@/app/(public)/(landing)/components/WhoWeBuildFor";
import backgroundOverlay from "@/app/(public)/shared/assets/background-overlay2.png";
import CaseStudyCarousel from "@/app/(public)/shared/components/CaseStudyCarousel";
import { CTA } from "@/app/(public)/shared/components/CTA";
import { Footer } from "@/app/(public)/shared/components/Footer";
import { Navbar } from "@/app/(public)/shared/components/Navbar";
import Testimonial from "@/app/(public)/shared/components/Testimonial";
import { ThemeProvider } from "@/app/(public)/shared/contexts/ThemeContext";
import "@/app/(public)/shared/tokens/animations.css";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/spacing.css";
import "@/app/(public)/shared/tokens/typography.css";
import type { ActiveLandingBanner } from "@/features/landing-banners/_types/landing-banner-ui-types";
import { fetchCaseStudies } from "@/lib/publications/api";
import { logBrokenImages } from "@/lib/publications/imageUtils";
import type { CaseStudy } from "@/lib/publications/types";
import Link from "next/link";
import { useEffect, useState } from "react";

interface LandingProps {
  banner: ActiveLandingBanner | null;
}

export default function Landing({ banner }: LandingProps) {
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>([]);

  useEffect(() => {
    const loadCaseStudies = async () => {
      try {
        const studies = await fetchCaseStudies();
        console.log("Loaded case studies:", studies);
        // Check for broken image URLs
        logBrokenImages(studies);

        // Filter out case studies with invalid data
        const validStudies = studies.filter(
          (study) => study && study.id && study.title && study.heroImage?.url && study.company?.logo?.url
        );

        console.log(`Filtered ${studies.length} case studies to ${validStudies.length} valid ones`);
        setCaseStudies(validStudies);
      } catch (err) {
        console.error("Error fetching case studies:", err);
      }
    };

    loadCaseStudies();
  }, []);

  const studiesForCarousel = caseStudies.filter((s) => !s.unlisted);

  return (
    <ThemeProvider>
      <div
        className={`overflow-x-clip bg-landing-primary relative${
          banner ? " [--promo-offset:5rem] lg:[--promo-offset:2.75rem]" : ""
        }`}
      >
        {/* Background image with opacity */}
        <div
          className="absolute inset-0 pointer-events-none z-0"
          style={{
            backgroundImage: `url(${backgroundOverlay.src})`,
            backgroundSize: "100% auto",
            backgroundPosition: "center top",
            backgroundRepeat: "no-repeat",
            opacity: 0.2,
          }}
        />

        {/* Content container with higher z-index */}
        <div className="relative z-2">
          <PromoBanner banner={banner} />
          <Navbar />
          <main className="container-page">
            <Hero />
            <Feature1 />
            <Testimonial variant="logo-row-featured" type="startup" />
            <Feature2 />
            <Feature3 />
            <Feature4 />
            <WhoWeBuildFor />
            {studiesForCarousel.length > 0 && (
              <CaseStudyCarousel variant="carousel" title="Customer Success Stories" caseStudies={studiesForCarousel} />
            )}
            <CTA />
          </main>
          <Footer />
        </div>

        {/* Floating Explore Button */}
        <Link
          href="/explore"
          className="fixed bottom-6 right-6 z-50 bg-indigo-400 text-white px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 font-semibold border border-white/20 backdrop-blur-sm"
          aria-label="Explore our platform"
        >
          Explore
        </Link>
      </div>
    </ThemeProvider>
  );
}
